import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Phone } from "lucide-react";

const isHttp = (u) => {
  try {
    const parsed = new URL(u);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
};

export default function PartnerFrame() {
  const [params] = useSearchParams();
  const url = params.get("url") || "";
  const name = params.get("name") || "Partner resource";
  const valid = useMemo(() => isHttp(url), [url]);
  const [loaded, setLoaded] = useState(false);
  const [showFallback, setShowFallback] = useState(false);

  useEffect(() => {
    if (!valid) return;
    const t = setTimeout(() => {
      if (!loaded) setShowFallback(true);
    }, 3500);
    return () => clearTimeout(t);
  }, [valid, loaded]);

  const host = valid ? new URL(url).hostname.replace(/^www\./, "") : "";

  return (
    <div
      data-testid="partner-frame"
      className="flex h-screen flex-col bg-ink font-sans text-fog"
    >
      {/* Branded top bar */}
      <header className="flex shrink-0 items-center justify-between gap-4 border-b border-white/10 bg-panel px-5 py-3 md:px-8">
        <Link
          to="/"
          data-testid="partner-frame-logo"
          className="whitespace-nowrap font-serif text-xl tracking-tight text-fog transition-colors duration-300 hover:text-steel md:text-2xl"
        >
          CallMe📲<span className="italic text-steel">Gerard.com</span>
        </Link>

        <div className="flex items-center gap-2 md:gap-3">
          <a
            data-testid="partner-frame-call"
            href="tel:3177076818"
            className="hidden items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs uppercase tracking-[0.2em] text-fog transition-colors duration-300 hover:border-navy hover:bg-navy sm:inline-flex"
          >
            <Phone className="h-3.5 w-3.5" />
            Call Me
          </a>
          {valid && (
            <a
              data-testid="partner-frame-open-original"
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-navy px-4 py-2 text-xs uppercase tracking-[0.2em] text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-steel md:px-5"
            >
              Open directly
              <ArrowUpRight className="h-3.5 w-3.5" />
            </a>
          )}
          <Link
            to="/"
            data-testid="partner-frame-back"
            className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs uppercase tracking-[0.2em] text-ash transition-[background-color,border-color,color] duration-300 hover:border-white/30 hover:text-fog md:px-5"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back
          </Link>
        </div>
      </header>

      {/* Context strip */}
      <div className="flex shrink-0 items-center justify-between gap-4 border-b border-white/5 bg-ink px-5 py-2 text-[11px] uppercase tracking-[0.22em] text-dim md:px-8">
        <span data-testid="partner-frame-label" className="truncate">
          {name}
          {host && <span className="text-steel"> · {host}</span>}
        </span>
        <span className="hidden whitespace-nowrap text-dim sm:inline">
          A CallMe📲Gerard partner
        </span>
      </div>

      {/* Embedded destination */}
      <div className="relative flex-1 bg-white">
        {valid ? (
          <>
            <iframe
              key={url}
              title={name}
              src={url}
              data-testid="partner-frame-iframe"
              onLoad={() => setLoaded(true)}
              className="h-full w-full border-0"
              allow="payment; clipboard-write; fullscreen"
            />
            {showFallback && !loaded && (
              <div
                data-testid="partner-frame-fallback"
                className="absolute inset-0 flex flex-col items-center justify-center gap-5 bg-ink px-6 text-center"
              >
                <p className="font-serif text-2xl text-fog md:text-3xl">
                  This partner page can't be shown inside the frame.
                </p>
                <p className="max-w-md text-sm text-ash">
                  For your security, {host || "this site"} opens in its own tab.
                  Click below to continue — you'll still be working with a
                  trusted CallMe📲Gerard partner.
                </p>
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="partner-frame-fallback-open"
                  className="inline-flex items-center gap-2 rounded-full bg-navy px-7 py-3 text-xs uppercase tracking-[0.2em] text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-steel"
                >
                  Continue to {host || "the page"}
                  <ArrowUpRight className="h-4 w-4" />
                </a>
              </div>
            )}
          </>
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-4 bg-ink px-6 text-center">
            <p className="font-serif text-2xl text-fog">Link unavailable</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 rounded-full bg-navy px-6 py-3 text-xs uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-steel"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to CallMe📲Gerard.com
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
